import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEye, lucideSend } from '@ng-icons/lucide';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { CheckboxComponent } from '@3ddv/software-division-components/backoffice/checkbox';
import { FileUploadComponent } from '@3ddv/software-division-components/backoffice/file-upload';
import { RichTextEditorComponent } from '@3ddv/software-division-components/generic/rich-text-editor';
import { ToastrService } from 'ngx-toastr';
import { concatMap, finalize, from, last, of, Subscription } from 'rxjs';
import { AuthStore } from '../../core/stores/auth.store';
import { GsLoaderComponent } from '../../shared/ui/loader/gs-loader.component';
import { AppDialogService } from '../layout/dialog/app-dialog.service';
import { ConfigurationService } from '../services/configuration.service';
import { UtilsService } from '../services/utils.service';
import { EmailsModalComponent } from './modals/emails/emails.component';
import { EmailTemplate } from './models/email.model';
import { EmailsService } from './services/emails.service';

type ImageField = 'logo' | 'headerBanner' | 'footerBanner';

const IMAGE_FIELDS: ImageField[] = ['logo', 'headerBanner', 'footerBanner'];

const PREVIEW_SCROLLBAR_STYLE = `<style>
  * { scrollbar-width: thin; scrollbar-color: #333 #dfe9eb; }
  *::-webkit-scrollbar { width: 5px; }
  *::-webkit-scrollbar-track { border-radius: 5px; background-color: #dfe9eb; }
  *::-webkit-scrollbar-track:hover { background-color: #b8c0c2; }
  *::-webkit-scrollbar-thumb { border-radius: 5px; background-color: #333; }
  body { margin: 0 auto !important; max-width: 640px; }
</style>`;

@Component({
  selector: 'app-emails',
  templateUrl: './emails.component.html',
  styleUrls: ['./emails.component.css'],
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    CheckboxComponent,
    FileUploadComponent,
    GsLoaderComponent,
    NgIcon,
    RichTextEditorComponent,
  ],
  viewProviders: [provideIcons({ lucideEye, lucideSend })],
})
export class EmailsComponent implements OnInit, OnDestroy {
  private emailsService = inject(EmailsService);
  private activatedRoute = inject(ActivatedRoute);
  private dialogService = inject(AppDialogService);
  private configurationService = inject(ConfigurationService);
  private toastr = inject(ToastrService);
  private utils = inject(UtilsService);
  private domSanitizer = inject(DomSanitizer);
  protected readonly authStore = inject(AuthStore);

  public data: EmailTemplate;
  private subscriptions: Array<Subscription> = [];
  private objectPreviewUrls = new Set<string>();

  public isLoading = false;
  public isSaving = false;
  public isRemovingFooterBanner = false;

  public livePreviewHtml: SafeHtml | null = null;
  public isLivePreviewLoading = false;
  public livePreviewFailed = false;

  public hashValidator = {
    logo: false,
    headerBanner: false,
    footerBanner: false,
    legalText: false,
    greetingText: false,
  };
  public imagePreviews: Record<ImageField, string | null> = {
    logo: null,
    headerBanner: null,
    footerBanner: null,
  };
  private originalImagePreviews: Record<ImageField, string | null> = {
    logo: null,
    headerBanner: null,
    footerBanner: null,
  };
  public pendingFiles: Partial<Record<ImageField, File>> = {};

  public legalTextControl = new FormControl<string>('', {
    nonNullable: true,
  });
  public greetingTextControl = new FormControl<string>('', {
    nonNullable: true,
  });
  public enabledControl = new FormControl<boolean>(false, {
    nonNullable: true,
  });

  ngOnInit(): void {
    this.data = this.activatedRoute.snapshot.data[
      'emailTemplate'
    ] as EmailTemplate;

    this.resetForm();
    this.loadLivePreview();
  }

  ngOnDestroy() {
    this.revokeObjectPreviewUrls();
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  public openModalPreview() {
    this.isLoading = true;
    const clientName = this.configurationService.configuration.clientName;

    const previewSub = this.emailsService
      .getPreview$(clientName)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: res => {
          this.dialogService.open(EmailsModalComponent, {
            size: 'xl',
            inputs: { data: res.data.template },
          });
        },
        error: error => {
          console.log(error);
          this.toastr.error(
            error?.error?.message || error?.message || 'Error loading preview'
          );
        },
      });

    this.subscriptions.push(previewSub);
  }

  public sendDemoEmail() {
    const sendEmailSub = this.emailsService.sendDemoEmail$().subscribe({
      next: res => {
        this.toastr.success(`Email sent to ${res.data.email}`);
      },
      error: error => {
        console.log(error);
        this.toastr.error(
          error?.error?.message || error?.message || 'Error loading preview'
        );
      },
    });

    this.subscriptions.push(sendEmailSub);
  }

  public onSave() {
    if (this.isSaving || !this.authStore.canEdit()) {
      return;
    }

    const hasErrors = this.checkInputs();

    if (hasErrors) {
      return;
    }

    this.data.emailData['legalText'] = this.legalTextControl.value;
    this.data.emailData['greetingText'] = this.greetingTextControl.value;
    this.data.isActive = this.enabledControl.value;

    this.isSaving = true;

    const fieldsToUpload = IMAGE_FIELDS.filter(
      field => this.pendingFiles[field] !== undefined
    );

    const uploadChain =
      fieldsToUpload.length === 0
        ? of(null)
        : from(fieldsToUpload).pipe(
            concatMap(field =>
              this.emailsService.uploadImage$(
                this.data.templateName,
                field,
                this.pendingFiles[field]
              )
            ),
            last()
          );

    const saveSub = uploadChain
      .pipe(
        concatMap(() => this.emailsService.update$(this.data)),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe({
        next: response => {
          this.data = response.data;
          this.pendingFiles = {};
          this.resetForm();
          this.loadLivePreview();
          this.toastr.success('Email template saved');
        },
        error: error => {
          console.log(error);
          this.toastr.error(
            error?.error?.message || error?.message || 'Unknown Error'
          );
        },
      });

    this.subscriptions.push(saveSub);
  }

  public dropzoneImage(field: ImageField): string | null {
    const preview = this.imagePreviews[field];

    return preview
      ? `url("${preview}") center / contain no-repeat #f7f7f7`
      : null;
  }

  public onFilesChange(files: File[], fieldName: ImageField) {
    const file = files[0];

    if (!file) {
      delete this.pendingFiles[fieldName];
      this.setImagePreview(
        fieldName,
        this.originalImagePreviews[fieldName],
        false
      );
      this.hashValidator[fieldName] = false;
      return;
    }

    if (!this.isValidImageFile(file)) {
      this.toastr.error(
        'Please select a valid image file (PNG, JPG, JPEG, GIF)'
      );
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    this.setImagePreview(fieldName, previewUrl, true);
    this.pendingFiles[fieldName] = file;
    this.hashValidator[fieldName] = false;
  }

  public onRemoveFooterBanner(event?: Event): void {
    event?.stopPropagation();

    if (this.isRemovingFooterBanner || this.isSaving) {
      return;
    }

    this.isRemovingFooterBanner = true;

    const removeSub = this.emailsService
      .deleteImage$(this.data.templateName, 'footerBanner')
      .pipe(
        finalize(() => {
          this.isRemovingFooterBanner = false;
        })
      )
      .subscribe({
        next: response => {
          this.data = response.data;
          this.setImagePreview('footerBanner', null, false);
          this.originalImagePreviews.footerBanner = null;
          delete this.pendingFiles.footerBanner;
          this.data.emailData['footerBanner'] = '';
          this.hashValidator.footerBanner = false;
          this.loadLivePreview();
        },
        error: error => {
          console.log(error);
          this.toastr.error(
            error?.error?.message || error?.message || 'Unknown Error'
          );
        },
      });

    this.subscriptions.push(removeSub);
  }

  private resetForm(): void {
    this.revokeObjectPreviewUrls();
    this.initializeImagePreviews();
    this.legalTextControl.setValue(
      (this.data.emailData['legalText'] as string) ?? ''
    );
    this.greetingTextControl.setValue(
      (this.data.emailData['greetingText'] as string) ?? ''
    );
    this.enabledControl.setValue(!!this.data.isActive);

    if (!this.authStore.canEdit()) {
      this.legalTextControl.disable();
      this.greetingTextControl.disable();
      this.enabledControl.disable();
    }
  }

  private loadLivePreview(): void {
    this.isLivePreviewLoading = true;
    this.livePreviewFailed = false;
    const clientName = this.configurationService.configuration.clientName;

    const previewSub = this.emailsService
      .getPreview$(clientName)
      .pipe(finalize(() => (this.isLivePreviewLoading = false)))
      .subscribe({
        next: res => {
          this.livePreviewHtml = this.domSanitizer.bypassSecurityTrustHtml(
            PREVIEW_SCROLLBAR_STYLE + res.data.template
          );
        },
        error: error => {
          console.log(error);
          this.livePreviewFailed = true;
        },
      });

    this.subscriptions.push(previewSub);
  }

  private checkInputs(): boolean {
    let hasErrors = false;

    Object.keys(this.hashValidator).forEach(value => {
      this.hashValidator[value] = false;
    });

    if (!this.isImageFieldValid('logo', true)) {
      this.hashValidator.logo = true;
      hasErrors = true;
    }

    if (!this.isImageFieldValid('headerBanner', true)) {
      this.hashValidator.headerBanner = true;
      hasErrors = true;
    }

    if (!this.isImageFieldValid('footerBanner', false)) {
      this.hashValidator.footerBanner = true;
      hasErrors = true;
    }

    const trimmedLegal = this.stripHtml(this.legalTextControl.value);
    if (!trimmedLegal) {
      this.legalTextControl.setValue('');
      this.hashValidator.legalText = true;
      hasErrors = true;
    }
    this.legalTextControl.setErrors(
      this.hashValidator.legalText ? { required: true } : null
    );

    const trimmedGreeting = this.stripHtml(this.greetingTextControl.value);
    if (!trimmedGreeting) {
      this.greetingTextControl.setValue('');
    }

    return hasErrors;
  }

  private stripHtml(html: string | null | undefined): string {
    return (html ?? '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  }

  private isImageFieldValid(field: ImageField, required: boolean): boolean {
    if (this.pendingFiles[field] !== undefined) {
      return true;
    }

    const preview = this.imagePreviews[field];

    if (this.utils.isEmpty(preview)) {
      return !required;
    }

    return this.utils.isValidImagePreview(preview);
  }

  private initializeImagePreviews(): void {
    IMAGE_FIELDS.forEach(field => {
      const storedValue = this.data.emailData[field] as string | undefined;
      const isStoredValueValid =
        storedValue !== undefined &&
        !this.utils.isEmpty(storedValue) &&
        this.utils.isValidImagePreview(storedValue);

      this.imagePreviews[field] = isStoredValueValid ? storedValue : null;
      this.originalImagePreviews[field] = this.imagePreviews[field];
    });
  }

  private setImagePreview(
    fieldName: ImageField,
    previewUrl: string | null,
    isObjectUrl: boolean
  ): void {
    const previousPreview = this.imagePreviews[fieldName];

    if (
      previousPreview !== null &&
      this.objectPreviewUrls.has(previousPreview)
    ) {
      URL.revokeObjectURL(previousPreview);
      this.objectPreviewUrls.delete(previousPreview);
    }

    this.imagePreviews[fieldName] = previewUrl;

    if (isObjectUrl && previewUrl !== null) {
      this.objectPreviewUrls.add(previewUrl);
    }
  }

  private revokeObjectPreviewUrls(): void {
    this.objectPreviewUrls.forEach(url => {
      URL.revokeObjectURL(url);
    });
    this.objectPreviewUrls.clear();
  }

  private isValidImageFile(file: File): boolean {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    const maxSize = 5 * 1024 * 1024;

    return allowedTypes.includes(file.type) && file.size <= maxSize;
  }
}
